import * as React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Search } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import {
  clampActiveIndex,
  groupCommands,
  isPaletteShortcut,
  moveActiveIndex,
  resolveCommands,
  type CommandSource,
} from './command-logic';

/**
 * Command palette (T-0140).
 *
 * Mirrors `.palette` in `docs/requirement-library/prototype/
 * selection.css`: a 600px dialog 12vh from the top with a text input and
 * grouped results. Cmd/Ctrl+K opens it from anywhere; Up / Down move the
 * highlight, Enter runs it, Esc closes.
 *
 * Commands come from the host, not from this file:
 *
 *   <CommandPaletteProvider> ... </CommandPaletteProvider>   // once
 *   useRegisterCommands(commands);   // in any screen, while mounted
 *
 * `commands` is a list (filtered here by label / keywords) or a function
 * of the query (for search-as-you-type results). Memoise it: the hook
 * re-registers whenever its identity changes.
 *
 * A source that searches the server (T-0227 record search) reads the
 * typed text with `useCommandPaletteQuery()`, fetches against it, and
 * registers a function source that returns whatever has arrived; the
 * query therefore lives in the provider, not in the dialog.
 *
 * `CommandPaletteTrigger` is the top-bar button that opens the palette
 * and shows the shortcut.
 *
 * Accessibility: Radix Dialog supplies the focus trap and focus return.
 * The input is a `combobox` controlling a `listbox`; the highlighted
 * option is exposed through `aria-activedescendant`, so focus stays in
 * the input while arrows move the highlight.
 */

interface CommandPaletteContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  query: string;
  setQuery: (query: string) => void;
  register: (source: CommandSource) => () => void;
}

const CommandPaletteContext = React.createContext<CommandPaletteContextValue | null>(null);

export interface CommandPaletteProviderProps {
  children: React.ReactNode;
  /** Input placeholder. */
  placeholder?: string;
  /** Disable the global Cmd/Ctrl+K listener (the trigger still works). */
  disableShortcut?: boolean;
}

export function CommandPaletteProvider({
  children,
  placeholder = 'Search or run a command…',
  disableShortcut = false,
}: CommandPaletteProviderProps) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [sources, setSources] = React.useState<ReadonlyArray<CommandSource>>([]);

  // Start clean every time the palette opens.
  React.useEffect(() => {
    if (open) setQuery('');
  }, [open]);

  const register = React.useCallback((source: CommandSource) => {
    setSources((prev) => [...prev, source]);
    return () => setSources((prev) => prev.filter((s) => s !== source));
  }, []);

  React.useEffect(() => {
    if (disableShortcut) return;
    const onKey = (e: KeyboardEvent) => {
      if (!isPaletteShortcut(e)) return;
      e.preventDefault();
      setOpen((o) => !o);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [disableShortcut]);

  const value = React.useMemo(
    () => ({ open, setOpen, query, setQuery, register }),
    [open, query, register],
  );

  return (
    <CommandPaletteContext.Provider value={value}>
      {children}
      <CommandPaletteDialog
        open={open}
        onOpenChange={setOpen}
        query={query}
        onQueryChange={setQuery}
        sources={sources}
        placeholder={placeholder}
      />
    </CommandPaletteContext.Provider>
  );
}

function useCommandPaletteContext(): CommandPaletteContextValue {
  const ctx = React.useContext(CommandPaletteContext);
  if (!ctx) throw new Error('Command palette hooks need <CommandPaletteProvider>');
  return ctx;
}

/** Open / close the palette from code. */
export function useCommandPalette(): { open: boolean; setOpen: (open: boolean) => void } {
  const { open, setOpen } = useCommandPaletteContext();
  return { open, setOpen };
}

/** What is typed in the palette, for sources that search the server. */
export function useCommandPaletteQuery(): { open: boolean; query: string } {
  const { open, query } = useCommandPaletteContext();
  return { open, query };
}

/**
 * Register commands for as long as the calling component is mounted.
 * Pass a memoised list or function.
 */
export function useRegisterCommands(source: CommandSource): void {
  const { register } = useCommandPaletteContext();
  React.useEffect(() => register(source), [register, source]);
}

interface CommandPaletteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  query: string;
  onQueryChange: (query: string) => void;
  sources: ReadonlyArray<CommandSource>;
  placeholder: string;
}

function CommandPaletteDialog({
  open,
  onOpenChange,
  query,
  onQueryChange,
  sources,
  placeholder,
}: CommandPaletteDialogProps) {
  const [active, setActive] = React.useState(0);
  const listId = React.useId();
  const listRef = React.useRef<HTMLDivElement>(null);

  // Start at the top every time the palette opens (the provider clears the query).
  React.useEffect(() => {
    if (open) setActive(0);
  }, [open]);

  const commands = React.useMemo(
    () => (open ? resolveCommands(sources, query) : []),
    [open, sources, query],
  );
  const groups = React.useMemo(() => groupCommands(commands), [commands]);
  const activeIndex = clampActiveIndex(active, commands.length);
  const optionId = (index: number) => `${listId}-opt-${index}`;

  React.useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`);
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [open, activeIndex]);

  const run = (index: number) => {
    const command = commands[index];
    if (!command) return;
    if (!command.keepOpen) onOpenChange(false);
    command.run();
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[60] bg-scrim" />
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed left-1/2 top-[12vh] z-[61] w-[600px] max-w-[calc(100vw-32px)] -translate-x-1/2 overflow-hidden rounded-modal bg-paper-0 shadow-modal focus:outline-none"
        >
          <Dialog.Title className="sr-only">Command palette</Dialog.Title>
          <div className="flex items-center gap-2.5 border-b border-ink-100 px-4 py-3.5 text-ink-500">
            <Search className="h-4 w-4 flex-none" aria-hidden="true" />
            <input
              role="combobox"
              aria-expanded="true"
              aria-controls={listId}
              aria-autocomplete="list"
              aria-activedescendant={commands.length > 0 ? optionId(activeIndex) : undefined}
              aria-label="Search or run a command"
              autoComplete="off"
              autoFocus
              value={query}
              placeholder={placeholder}
              onChange={(e) => {
                onQueryChange(e.target.value);
                setActive(0);
              }}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setActive(moveActiveIndex(activeIndex, 1, commands.length));
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setActive(moveActiveIndex(activeIndex, -1, commands.length));
                } else if (e.key === 'Enter') {
                  e.preventDefault();
                  run(activeIndex);
                }
              }}
              className="min-w-0 flex-1 border-0 bg-transparent text-h3 text-ink-900 placeholder:text-ink-300 focus:outline-none"
            />
            <kbd className="kbd">Esc</kbd>
          </div>
          <div
            ref={listRef}
            id={listId}
            role="listbox"
            aria-label="Commands"
            className="max-h-[360px] overflow-y-auto p-1.5"
          >
            {commands.length === 0 ? (
              <div className="px-6 py-14 text-center text-body-sm text-ink-500">No matches.</div>
            ) : (
              groups.map((g) => (
                <div key={g.group} role="group" aria-label={g.group}>
                  <div
                    aria-hidden="true"
                    className="px-2.5 pb-1 pt-2 text-meta font-medium uppercase tracking-wide text-ink-500"
                  >
                    {g.group}
                  </div>
                  {g.entries.map(({ command, index }) => {
                    const on = index === activeIndex;
                    return (
                      <div
                        key={command.id}
                        id={optionId(index)}
                        role="option"
                        aria-selected={on}
                        data-index={index}
                        onMouseMove={() => {
                          if (!on) setActive(index);
                        }}
                        onClick={() => run(index)}
                        className={cn(
                          'flex h-9 cursor-pointer items-center gap-2.5 rounded-chip px-2.5 text-body-sm',
                          on ? 'bg-paper-100 text-ink-900' : 'text-ink-700',
                        )}
                      >
                        {command.icon ? (
                          <span className="flex-none text-ink-500" aria-hidden="true">
                            {command.icon}
                          </span>
                        ) : null}
                        {command.mono ? (
                          <span className="flex-none font-mono text-meta text-ink-500">
                            {command.mono}
                          </span>
                        ) : null}
                        <span className="min-w-0 flex-1 truncate">{command.label}</span>
                        {command.hint ? (
                          <span className="flex-none text-meta text-ink-500">{command.hint}</span>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              ))
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export interface CommandPaletteTriggerProps {
  /** Visible label. Default "Search or run a command…". */
  label?: string;
  className?: string;
}

/** True on Apple platforms, where the shortcut is Cmd rather than Ctrl. */
function isApplePlatform(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Mac|iPhone|iPad/i.test(navigator.platform || navigator.userAgent);
}

/** Top-bar button that opens the palette and shows the shortcut. */
export function CommandPaletteTrigger({
  label = 'Search or run a command…',
  className,
}: CommandPaletteTriggerProps) {
  const { open, setOpen } = useCommandPaletteContext();
  const shortcut = isApplePlatform() ? '⌘K' : 'Ctrl K';
  return (
    <button
      type="button"
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-keyshortcuts="Meta+K Control+K"
      onClick={() => setOpen(true)}
      className={cn(
        'inline-flex h-8 w-[280px] items-center gap-2 rounded-card border border-ink-200 bg-paper-50 px-2.5 text-body-sm text-ink-300 hover:border-ink-300',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
        className,
      )}
    >
      <Search className="h-3.5 w-3.5 flex-none text-ink-500" aria-hidden="true" />
      <span className="min-w-0 flex-1 truncate text-left">{label}</span>
      <kbd className="kbd">{shortcut}</kbd>
    </button>
  );
}
